import { ActivityParentEntityId, CustomListKey } from "@sp-crm/core";
import { EntityActivityClientStatus } from "components/entity-activity/entity-activity-client-status";
import {
    EntityActivityCollaboratorAdded,
    EntityActivityCollaboratorRemoved,
} from "components/entity-activity/entity-activity-collaborator";
import {
    EntityActivityEntityCreate,
    EntityActivityEntityDeleted,
    EntityActivityEntityRestored,
} from "components/entity-activity/entity-activity-entity";
import { EntityActivityEntryFileUpload } from "components/entity-activity/entity-activity-file";
import { EntityActivityMessageSent } from "components/entity-activity/entity-activity-message";
import { EntityActivityVoiceCall } from "components/entity-activity/entity-activity-voice-call";
import {
    EntityActivityOrganizationMemberAdded,
    EntityActivityOrganizationMemberRemoved,
} from "components/entity-activity/entity-activity-organization";
import { EntityActivityReassign } from "components/entity-activity/entity-activity-reassign";
import {
    EntityActivityTaskComplete,
    EntityActivityTaskCreate,
    EntityActivityTaskDelete,
    EntityActivityTaskReassign,
    EntityActivityTaskUncomplete,
    EntityActivityTaskUpdate,
} from "components/entity-activity/entity-activity-task";
import {
    ActivityEntityType,
    EntityActivityEntry,
} from "components/entity-activity/types";
import { ActivityType } from "generated/graphql";
import React from "react";

interface Props {
    entityId: ActivityParentEntityId;
    entityType: ActivityEntityType;
    activity: EntityActivityEntry;
}

export const UnifiedTimelineActivityEntry: React.FC<Props> = props => {
    const { entityId, entityType, activity } = props;
    const activityType = activity.activity.activityType;

    if (activityType === ActivityType.TaskCreate) {
        return <EntityActivityTaskCreate entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.EntityFieldChange) {
        if (
            (entityType === "Client" || entityType === "HomeCareLead") &&
            activity.activity.metadata?.events?.some(
                e =>
                    e.entityFieldChange?.field === "status" ||
                    e.entityFieldChange?.field === "statusListItemId",
            )
        ) {
            const customListKey =
                entityType === "Client"
                    ? CustomListKey.ClientStatus
                    : CustomListKey.HomeCareLeadStatus;
            return (
                <EntityActivityClientStatus
                    entityId={entityId}
                    activity={activity}
                    customListKey={customListKey}
                />
            );
        }
        return null;
    }
    if (activityType === ActivityType.EntityCreate) {
        return (
            <EntityActivityEntityCreate
                entityId={entityId}
                entityType={entityType}
                activity={activity}
            />
        );
    }
    if (activityType === ActivityType.TaskUpdate) {
        return <EntityActivityTaskUpdate entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.TaskDelete) {
        return <EntityActivityTaskDelete entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.TaskComplete) {
        return <EntityActivityTaskComplete entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.TaskUncomplete) {
        return <EntityActivityTaskUncomplete entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.TaskReassign) {
        return <EntityActivityTaskReassign entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.FileUpload) {
        return <EntityActivityEntryFileUpload entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.MessageSent) {
        return <EntityActivityMessageSent entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.OutgoingCall) {
        return <EntityActivityVoiceCall entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.OrganizationMemberAdded) {
        return (
            <EntityActivityOrganizationMemberAdded
                entityId={entityId}
                activity={activity}
            />
        );
    }
    if (activityType === ActivityType.OrganizationMemberRemoved) {
        return (
            <EntityActivityOrganizationMemberRemoved
                entityId={entityId}
                activity={activity}
            />
        );
    }
    if (activityType === ActivityType.EntityReassign) {
        return <EntityActivityReassign entityId={entityId} activity={activity} />;
    }
    if (activityType === ActivityType.EntityCollaboratorAdded) {
        return (
            <EntityActivityCollaboratorAdded entityId={entityId} activity={activity} />
        );
    }
    if (activityType === ActivityType.EntityCollaboratorRemoved) {
        return (
            <EntityActivityCollaboratorRemoved entityId={entityId} activity={activity} />
        );
    }
    if (activityType === ActivityType.CommunityRestored) {
        return (
            <EntityActivityEntityRestored
                entityId={entityId}
                entityType={entityType}
                activity={activity}
            />
        );
    }
    if (activityType === ActivityType.CommunityDeleted) {
        return (
            <EntityActivityEntityDeleted
                entityId={entityId}
                entityType={entityType}
                activity={activity}
            />
        );
    }

    return null;
};
